/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type { ButtonHTMLAttributes, ElementType, HTMLAttributes, MouseEventHandler, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { SplitFeatureSectionPicture } from '../marketing/split-feature-section/split-feature-section'

const DEFAULT_IMAGE_PICTURE_SIZES = '(min-width: 768px) 33vw, 100vw'

export type PhotoTitleCardRootProps = Omit<HTMLAttributes<HTMLElement>, 'children'> &
  Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
    /** Root element; defaults to `button`. */
    as?: ElementType
  } /** Passthrough for polymorphic roots (e.g. router `Link` `to` / `search`). */ & Record<string, unknown>

export interface PhotoTitleCardProps {
  /** Static URL when `imagePicture` is not used (e.g. remote media). */
  imageUrl?: string
  /** vite-imagetools `&as=picture` (`format=avif;webp&as=picture`, etc.). */
  imagePicture?: SplitFeatureSectionPicture
  imagePictureSizes?: string
  imageAlt: string
  imagePositionY?: string | number
  title: ReactNode
  subtitle?: ReactNode
  onClick?: MouseEventHandler<HTMLElement>
  rootProps?: PhotoTitleCardRootProps
}

export function PhotoTitleCard({
  imageUrl,
  imagePicture,
  imagePictureSizes = DEFAULT_IMAGE_PICTURE_SIZES,
  imageAlt,
  imagePositionY = 'center',
  title,
  subtitle,
  onClick,
  rootProps,
}: PhotoTitleCardProps) {
  const {
    as: RootAs = 'button',
    onClick: rootOnClick,
    className: rootClassName,
    type: rootType,
    ...restRoot
  } = rootProps ?? {}

  const Root = RootAs as ElementType

  const objectPosition = typeof imagePositionY === 'number' ? `center ${imagePositionY}px` : `center ${imagePositionY}`

  return (
    <Root
      className={cn(
        'group relative block w-full cursor-pointer overflow-hidden rounded-3xl border border-border',
        'h-[200px] md:h-full',
        'transition-[border-color,box-shadow]',
        'hover:border-foreground/50 hover:shadow-sm',
        'focus-visible:outline-1 focus-visible:outline-primary focus-visible:outline-offset-2',
        rootClassName
      )}
      onClick={(onClick ?? rootOnClick) as MouseEventHandler<never>}
      {...restRoot}
      {...(typeof Root === 'string' && Root === 'button' ? { type: rootType ?? ('button' as const) } : {})}
    >
      {imagePicture ? (
        <picture className="pointer-events-none absolute inset-0 size-full">
          {Object.entries(imagePicture.sources).map(([format, srcSet]) => (
            <source key={format} sizes={imagePictureSizes} srcSet={srcSet} type={`image/${format}`} />
          ))}
          <img
            alt={imageAlt}
            className={cn(
              'h-full w-full object-cover',
              'transition-transform duration-700 ease-in-out group-hover:scale-[1.08]'
            )}
            decoding="async"
            fetchPriority="high"
            height={imagePicture.img.h}
            sizes={imagePictureSizes}
            src={imagePicture.img.src}
            style={{ objectPosition }}
            width={imagePicture.img.w}
          />
        </picture>
      ) : (
        <img
          alt={imageAlt}
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full object-cover',
            'transition-transform duration-700 ease-in-out group-hover:scale-[1.08]'
          )}
          decoding="async"
          fetchPriority="high"
          height="100%"
          max-width="none"
          src={imageUrl}
          style={{ objectPosition }}
          width="100%"
        />
      )}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-black/35" />
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-0 bg-black/70 px-4 py-1">
        <h2 className="font-bold text-2xl text-white uppercase">{title}</h2>
        {subtitle != null ? (
          <div className="flex flex-row items-center gap-2">
            {typeof subtitle === 'string' || typeof subtitle === 'number' ? (
              <span className="font-semibold text-base text-primary/80">{subtitle}</span>
            ) : (
              subtitle
            )}
          </div>
        ) : null}
      </div>
    </Root>
  )
}
